// src/components/COAStagedUploadModal/COAStagedUploadModal.js
// Dropzone modal with a staged list — user enters SKU + weight before processing
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useLicense } from "../../context/licenseContext";
import { useToast } from "../../context/toastContext";
import { useCoaJobs } from "../../context/coaJobsContext";
import apiClient from "../../api/apiClient";
import { toGrams } from "../../utils/weightUtils";
import "./COAStagedUploadModal.css";

const WEIGHT_UNITS = ["g", "oz", "lb", "units"];
 
const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
    "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
    "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC", 
]; 

function generateId() {
    return Math.random().toString(36).slice(2, 10);
}

const COAStagedUploadModal = ({ onClose, fetchCoas, setCOAProcessing }) => {
    const { currentLicense } = useLicense();
    const toast = useToast();
    const { submitJobs } = useCoaJobs();
    const [stagedFiles, setStagedFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const ALLOWED_TYPES = ["application/pdf", "application/x-pdf", "image/jpeg", "image/png", "image/webp"];

    const onDrop = useCallback((acceptedFiles) => {
        const valid = acceptedFiles.filter((f) => ALLOWED_TYPES.includes(f.type));
        if (valid.length !== acceptedFiles.length) {
            toast.warning("Only PDF and image files (JPEG, PNG, WebP) are accepted.");
        }
        if (valid.length === 0) return;
        setStagedFiles((prev) => [
            ...prev,
            ...valid.map((file) => ({
                id: generateId(),
                file,
                sku: "",
                cultivatorProducerName: "",
                cultivatorState: "",
                harvestDate: "",
                weight: "",
                unit: "lb",
                status: "pending", // pending | uploading | processing | done | error
                error: null,
            })),
        ]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/pdf": [".pdf"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
        },
        disabled: isProcessing,
    });

    const updateField = (id, field, value) => {
        setStagedFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
        );
    };

    const removeFile = (id) => {
        setStagedFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const setFileState = (id, status, error = null) => {
        setStagedFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, status, error } : f))
        );
    };

    const canProcess =
        stagedFiles.length > 0 &&
        stagedFiles.every((f) => f.weight.trim() && f.cultivatorProducerName.trim() && f.cultivatorState && f.harvestDate.trim()) &&
        !isProcessing;

    const handleProcessAll = async () => {
        setIsProcessing(true);

        const pending = stagedFiles.filter((f) => f.status !== "done");
        pending.forEach((f) => setFileState(f.id, "uploading"));

        try {
            const formData = new FormData();
            const fileMetadata = [];

            for (const staged of pending) {
                formData.append("files", staged.file);
                const weightInGrams = staged.unit === "units"
                    ? staged.weight.trim()
                    : String(toGrams(parseFloat(staged.weight.trim()), staged.unit));
                const unitToSend = staged.unit === "units" ? "units" : "g";
                fileMetadata.push({
                    sku: staged.sku.trim() || null,
                    cultivator_producer_name: staged.cultivatorProducerName.trim(),
                    cultivator_state: staged.cultivatorState,
                    harvest_date: staged.harvestDate.trim(),
                    weight: weightInGrams,
                    weight_unit: unitToSend,
                });
            }

            formData.append("file_metadata", JSON.stringify(fileMetadata));

            const res = await apiClient.post("/processCOA/", formData, {
                headers: {
                    company: currentLicense.company,
                    state: currentLicense.state,
                    industry: currentLicense.industry,
                    licenseNo: currentLicense.licenseNo,
                },
            });

            const { jobId } = res.data || {};
            if (!jobId) throw new Error("No jobId returned from server.");

            pending.forEach((f) => setFileState(f.id, "done"));
            submitJobs([{
                jobId,
                pollUrl: `/processCOA/job/${jobId}`,
                fileName: pending.map((f) => f.file.name).join(", "),
            }]);
            setSubmitted(true);
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Upload failed";
            pending.forEach((f) => setFileState(f.id, "error", msg));
        }

        setIsProcessing(false);
    };

    const statusBadge = (status) => {
        const map = {
            pending:    { label: "Ready",      cls: "csm-badge--pending" },
            uploading:  { label: "Uploading…",  cls: "csm-badge--active" },
            done:       { label: "Submitted",   cls: "csm-badge--done" },
            error:      { label: "Error",       cls: "csm-badge--error" },
        };
        const { label, cls } = map[status] || map.pending;
        return <span className={`csm-badge ${cls}`}>{label}</span>;
    };

    const hasErrors = stagedFiles.some((f) => f.status === "error");

    if (submitted) {
        return (
            <div className="csm-backdrop" onClick={onClose}>
                <div className="csm csm--submitted" onClick={(e) => e.stopPropagation()}>
                    <div className="csm__header">
                        <h3 className="csm__title">Processing Started</h3>
                        <button className="csm__close" onClick={onClose} aria-label="Close">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div className="csm__body csm__body--submitted">
                        <svg className="csm__submitted-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 2L11 13"/>
                            <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
                        </svg>
                        <h4 className="csm__submitted-heading">
                            Your COA{stagedFiles.length !== 1 ? "s are" : " is"} being processed
                        </h4>
                        <p className="csm__submitted-text">
                            You'll receive an email when processing is complete. This typically takes 1–2 minutes. You can continue using the app in the meantime.
                        </p>
                    </div>
                    <div className="csm__footer">
                        <button className="csm__btn csm__btn--submit" onClick={onClose}>
                            Got it
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="csm-backdrop" onClick={!isProcessing ? onClose : undefined}>
            <div className="csm" onClick={(e) => e.stopPropagation()}>
                <div className="csm__header">
                    <h3 className="csm__title">Upload COA</h3>
                    <button className="csm__close" onClick={onClose} disabled={isProcessing} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="csm__body">
                    {/* Dropzone */}
                    <div
                        {...getRootProps()}
                        className={`csm-dropzone${isDragActive ? " csm-dropzone--active" : ""}${isProcessing ? " csm-dropzone--disabled" : ""}`}
                    >
                        <input {...getInputProps()} />
                        <svg className="csm-dropzone__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <p className="csm-dropzone__primary">
                            {isDragActive ? "Drop files here" : "Drop files here or click to browse"}
                        </p>
                        <p className="csm-dropzone__secondary">PDF or image files (JPEG, PNG, WebP)</p>
                    </div>

                    {/* Staged file list */}
                    {stagedFiles.length > 0 && (
                        <div className="csm__list-section">
                            <div className="csm__section-label">
                                Staged Files ({stagedFiles.length})
                            </div>
                            <div className="csm__list">
                                {stagedFiles.map((staged) => (
                                    <div key={staged.id} className={`csm__item${staged.status === "error" ? " csm__item--error" : ""}`}>
                                        <div className="csm__item-name" title={staged.file.name}>
                                            {staged.file.name}
                                            {statusBadge(staged.status)}
                                        </div>
                                        <div className="csm__item-fields">
                                            <div className="csm__field">
                                                <label className="csm__field-label">SKU <span className="csm__field-optional">(optional)</span></label>
                                                <input
                                                    className="csm__field-input"
                                                    type="text"
                                                    placeholder="e.g. BLU-001"
                                                    value={staged.sku}
                                                    onChange={(e) => updateField(staged.id, "sku", e.target.value)}
                                                    disabled={isProcessing || staged.status === "done"}
                                                />
                                            </div>
                                            <div className="csm__field">
                                                <label className="csm__field-label">Weight</label>
                                                <div className="csm__weight-row">
                                                    <input
                                                        className="csm__field-input csm__field-input--weight"
                                                        type="number"
                                                        min="0"
                                                        step="any"
                                                        placeholder="e.g. 28"
                                                        value={staged.weight}
                                                        onChange={(e) => updateField(staged.id, "weight", e.target.value)}
                                                        disabled={isProcessing || staged.status === "done"}
                                                    />
                                                    <select
                                                        className="csm__field-select"
                                                        value={staged.unit}
                                                        onChange={(e) => updateField(staged.id, "unit", e.target.value)}
                                                        disabled={isProcessing || staged.status === "done"}
                                                    >
                                                        {WEIGHT_UNITS.map((u) => (
                                                            <option key={u} value={u}>{u}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <button
                                                className="csm__item-remove"
                                                onClick={() => removeFile(staged.id)}
                                                disabled={isProcessing}
                                                aria-label="Remove"
                                                title="Remove"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                    <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="csm__item-fields">
                                            <div className="csm__field">
                                                <label className="csm__field-label">Cultivator</label>
                                                <input
                                                    className="csm__field-input"
                                                    type="text"
                                                    placeholder="e.g. Green Valley Farms"
                                                    value={staged.cultivatorProducerName}
                                                    onChange={(e) => updateField(staged.id, "cultivatorProducerName", e.target.value)}
                                                    disabled={isProcessing || staged.status === "done"}
                                                />
                                            </div>
                                            <div className="csm__field csm__field--state">
                                                <label className="csm__field-label">State</label>
                                                <select
                                                    className="csm__field-select csm__field-select--full"
                                                    value={staged.cultivatorState}
                                                    onChange={(e) => updateField(staged.id, "cultivatorState", e.target.value)}
                                                    disabled={isProcessing || staged.status === "done"}
                                                >
                                                    <option value="">—</option>
                                                    {US_STATES.map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="csm__field">
                                                <label className="csm__field-label">Harvest Date</label>
                                                <input
                                                    className="csm__field-input"
                                                    type="date"
                                                    value={staged.harvestDate}
                                                    onChange={(e) => updateField(staged.id, "harvestDate", e.target.value)}
                                                    disabled={isProcessing || staged.status === "done"}
                                                />
                                            </div>
                                        </div>
                                        {staged.error && (
                                            <p className="csm__item-error">{staged.error}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {hasErrors && !isProcessing && (
                        <p className="csm__error-msg">Some files failed to upload. Fix errors and retry, or remove failed items.</p>
                    )}
                </div>

                <div className="csm__footer">
                    <button
                        className="csm__btn csm__btn--cancel"
                        onClick={onClose}
                        disabled={isProcessing}
                    >
                        Cancel
                    </button>
                    <button
                        className="csm__btn csm__btn--submit"
                        onClick={handleProcessAll}
                        disabled={!canProcess}
                    >
                        {isProcessing ? "Submitting…" : `Process All (${stagedFiles.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default COAStagedUploadModal;
